/**
 * Componente: Formulario Paso 1 — Datos de la Granja y Predio Avícola
 * Rediseñado fielmente según desing-paso1-signup.html & design-paso1-signup-movil.html (#c25ee8)
 */

'use client';

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Link from 'next/link';
import { useForm, FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import {
  getCountriesAsync,
  getStatesAsync,
  getCitiesAsync,
  getQuickCountryIso,
} from '@/shared/services/geo';
import { GeoOption } from '@/shared/components/ui';
import * as Flags from 'country-flag-icons/react/3x2';
import { hasFlag } from 'country-flag-icons';
import {
  paso1GranjaSchema,
  Paso1GranjaInput,
  Paso1GranjaOutput,
} from '../schemas/onboarding.schema';
import { Paso1GranjaFormData } from '../types/onboarding.types';
import { PaisModalSelector, PaisItem } from './PaisModalSelector';
import { EstadoModalSelector } from './EstadoModalSelector';
import { CiudadModalSelector } from './CiudadModalSelector';
import { PorQuePedimosDatosModal } from './PorQuePedimosDatosModal';
import styles from './Step1GranjaForm.module.css';

function TriggerCountryFlag({ isoCode }: { isoCode: string }) {
  const upper = (isoCode || '').toUpperCase();
  if (hasFlag(upper)) {
    const FlagComp = (
      Flags as Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>>
    )[upper];
    if (FlagComp) {
      return <FlagComp className="w-full h-full object-cover" />;
    }
  }
  return <span className="text-[10px]">🌐</span>;
}

interface Step1GranjaFormProps {
  initialData: Paso1GranjaFormData;
  onSubmit: (data: Paso1GranjaFormData) => Promise<boolean>;
  onCancel?: () => void;
}

export function Step1GranjaForm({
  initialData,
  onSubmit,
}: Step1GranjaFormProps) {
  const [, setCountries] = useState<GeoOption[]>([]);
  const [states, setStates] = useState<GeoOption[]>([]);
  const [cities, setCities] = useState<GeoOption[]>([]);
  const [isSubmittingLocal, setIsSubmittingLocal] = useState<boolean>(false);
  const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
  const [isCountryModalOpen, setIsCountryModalOpen] = useState<boolean>(false);
  const [isStateModalOpen, setIsStateModalOpen] = useState<boolean>(false);
  const [isCityModalOpen, setIsCityModalOpen] = useState<boolean>(false);
  const [isLoadingStates, setIsLoadingStates] = useState<boolean>(false);
  const [isLoadingCities, setIsLoadingCities] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<Paso1GranjaInput, unknown, Paso1GranjaOutput>({
    resolver: zodResolver(paso1GranjaSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      nombre_granja: initialData.nombre_granja || '',
      pais: initialData.pais || 'Colombia',
      departamento_estado: initialData.departamento_estado || '',
      municipio_ciudad: initialData.municipio_ciudad || '',
      idioma: initialData.idioma || 'es',
      capacidad_total_aves: initialData.capacidad_total_aves ?? null,
      sistema_alojamiento: initialData.sistema_alojamiento || '',
    },
  });

  const selectedCountry = watch('pais');
  const selectedState = watch('departamento_estado');
  const selectedCity = watch('municipio_ciudad');

  const currentCountryIso = useMemo(() => {
    return getQuickCountryIso(selectedCountry) || 'CO';
  }, [selectedCountry]);

  const handleSelectCountryFromModal = useCallback(
    (pais: PaisItem) => {
      setValue('pais', pais.name, { shouldValidate: true });
      clearErrors('pais');
      setValue('departamento_estado', '');
      setValue('municipio_ciudad', '');
    },
    [setValue, clearErrors]
  );

  const handleSelectStateFromModal = useCallback(
    (stateName: string) => {
      setValue('departamento_estado', stateName, { shouldValidate: true });
      clearErrors('departamento_estado');
      setValue('municipio_ciudad', '');
    },
    [setValue, clearErrors]
  );

  const handleSelectCityFromModal = useCallback(
    (cityName: string) => {
      setValue('municipio_ciudad', cityName, { shouldValidate: true });
      clearErrors('municipio_ciudad');
    },
    [setValue, clearErrors]
  );

  // Cargar catálogo de países
  useEffect(() => {
    let mounted = true;
    async function loadCountries() {
      try {
        const countryList = await getCountriesAsync();
        if (mounted && countryList.length > 0) {
          setCountries(countryList);
        }
      } catch {
        // Fallback usa catálogo interno
      }
    }
    loadCountries();
    return () => {
      mounted = false;
    };
  }, []);

  // Cargar estados cuando cambia país
  useEffect(() => {
    let mounted = true;
    async function loadStates() {
      if (!selectedCountry || selectedCountry === 'Otro') {
        setStates([]);
        return;
      }
      setIsLoadingStates(true);
      try {
        const stateList = await getStatesAsync(selectedCountry);
        if (mounted) {
          setStates(stateList);
        }
      } catch {
        if (mounted) setStates([]);
      } finally {
        if (mounted) {
          setIsLoadingStates(false);
        }
      }
    }
    loadStates();
    return () => {
      mounted = false;
    };
  }, [selectedCountry]);

  // Cargar ciudades cuando cambia estado
  useEffect(() => {
    let mounted = true;
    async function loadCities() {
      if (!selectedCountry || !selectedState) {
        setCities([]);
        return;
      }
      setIsLoadingCities(true);
      try {
        const cityList = await getCitiesAsync(selectedCountry, selectedState);
        if (mounted) {
          setCities(cityList);
        }
      } catch {
        if (mounted) setCities([]);
      } finally {
        if (mounted) {
          setIsLoadingCities(false);
        }
      }
    }
    loadCities();
    return () => {
      mounted = false;
    };
  }, [selectedCountry, selectedState]);

  const handleFormSubmit = async (data: Paso1GranjaOutput) => {
    setIsSubmittingLocal(true);
    try {
      await onSubmit(data as Paso1GranjaFormData);
    } finally {
      setIsSubmittingLocal(false);
    }
  };

  const onFormInvalid = (formErrors: FieldErrors<Paso1GranjaInput>) => {
    const errorKeys = Object.keys(formErrors) as (keyof Paso1GranjaInput)[];
    if (errorKeys.length > 0) {
      const firstKey = errorKeys[0];
      const firstMsg =
        formErrors[firstKey]?.message || 'Por favor completa los datos de tu granja.';
      toast.error(firstMsg);
    }
  };

  return (
    <div className={styles.formWrapper}>
      {/* ─── Título y Subtítulo Fiel a design-paso1-signup-movil.html ─── */}
      <div className="text-center mb-2 sm:mb-4">
        <h1
          className="text-[28px] sm:text-[32px] lg:text-[34px] leading-none font-bold text-gray-900 tracking-normal"
          style={{ fontFamily: "'Caveat', cursive", fontWeight: 700, lineHeight: 1.05 }}
        >
          ¡Hola!, Vamos a crear tu{' '}
          <span className={styles.highlightMarker}>granja</span>
        </h1>
        <p className="text-gray-500 text-xs mt-1 leading-snug max-w-xs sm:max-w-sm mx-auto">
          Comencemos configurando la información básica de tu granja.
        </p>
      </div>

      {/* ─── Stepper de 3 Pasos con Amplio Padding Superior e Inferior ─── */}
      <nav aria-label="Progreso de registro" className={styles.stepperNav} data-purpose="step-indicator">
        <div className={styles.stepperContainer}>
          {/* Paso 1: Activo */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="w-6 h-6 rounded-full bg-[#c25ee8] text-white text-xs font-bold flex items-center justify-center shadow-sm ring-2 ring-purple-100">
              1
            </span>
            <span className="text-xs font-bold text-[#c25ee8]">Granja</span>
          </div>
          {/* Conector 1-2 */}
          <div className="flex-1 h-[2px] bg-gray-200 mx-2" />
          {/* Paso 2: Inactivo */}
          <div className="flex items-center gap-2 flex-shrink-0 opacity-60">
            <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-500 text-xs font-semibold flex items-center justify-center border border-gray-200">
              2
            </span>
            <span className="text-xs font-medium text-gray-500">Cuenta</span>
          </div>
          {/* Conector 2-3 */}
          <div className="flex-1 h-[2px] bg-gray-200 mx-2" />
          {/* Paso 3: Inactivo */}
          <div className="flex items-center gap-2 flex-shrink-0 opacity-60">
            <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-500 text-xs font-semibold flex items-center justify-center border border-gray-200">
              3
            </span>
            <span className="text-xs font-medium text-gray-500">Seguridad</span>
          </div>
        </div>
      </nav>

      {/* ─── Formulario ─── */}
      <form
        onSubmit={handleSubmit(handleFormSubmit, onFormInvalid)}
        className="w-full max-w-[400px] mx-auto text-left flex flex-col gap-3"
        noValidate
      >
        <input type="hidden" {...register('pais')} />
        <input type="hidden" {...register('departamento_estado')} />
        <input type="hidden" {...register('municipio_ciudad')} />
        <input type="hidden" {...register('idioma')} />
        {/* Nombre de la granja */}
        <div className="flex flex-col gap-1">
          <label
            className="flex items-center gap-2 text-xs font-bold text-gray-700 mb-1"
            htmlFor="farm-name"
          >
            <span className="material-symbols-outlined text-[15px] text-[#c25ee8]">
              agriculture
            </span>{' '}
            ¿Cómo se llama tu granja?
          </label>
          <div className="relative">
            <input
              id="farm-name"
              type="text"
              placeholder="Ej. Granja La Esperanza"
              className={`${styles.inputField} ${
                errors.nombre_granja ? styles.inputError : ''
              }`}
              {...register('nombre_granja')}
            />
          </div>
          {errors.nombre_granja && (
            <p className="text-[10px] font-medium text-red-500 pl-1 mt-0.5">
              {errors.nombre_granja.message}
            </p>
          )}
        </div>

        {/* Dual Field: País y Departamento / Estado en 2 columnas */}
        <div className="grid grid-cols-2 gap-3">
          {/* País */}
          <div>
            <label
              className="flex items-center gap-1.5 text-xs font-bold text-gray-700 mb-1 truncate"
              htmlFor="country"
            >
              <span className="material-symbols-outlined text-[15px] text-[#c25ee8]">
                public
              </span>{' '}
              <span className="truncate">¿En qué país estás?</span>
            </label>
            <div className="relative">
              <button
                type="button"
                id="country"
                onClick={() => setIsCountryModalOpen(true)}
                className={`${styles.countrySelectorButton} ${
                  errors.pais ? styles.inputError : ''
                }`}
                aria-haspopup="dialog"
                aria-expanded={isCountryModalOpen}
              >
                <div className="flex items-center gap-1.5 min-w-0 pr-1">
                  {currentCountryIso && (
                    <span className="flex-shrink-0 inline-flex items-center justify-center w-4.5 h-3 rounded-[2px] overflow-hidden shadow-2xs border border-black/10">
                      <TriggerCountryFlag isoCode={currentCountryIso} />
                    </span>
                  )}
                  <span
                    className={`text-xs sm:text-sm truncate ${
                      selectedCountry ? 'text-gray-800 font-medium' : 'text-gray-400'
                    }`}
                  >
                    {selectedCountry || 'Selecciona país'}
                  </span>
                </div>
                <span className="material-symbols-outlined text-[15px] text-gray-400 pointer-events-none flex-shrink-0">
                  expand_more
                </span>
              </button>
            </div>
            {errors.pais && (
              <p className="text-[10px] font-medium text-red-500 pl-1 mt-0.5">
                {errors.pais.message}
              </p>
            )}
          </div>

          {/* Estado / Departamento (Sin icono interno) */}
          <div>
            <label
              className="flex items-center gap-1.5 text-xs font-bold text-gray-700 mb-1 truncate"
              htmlFor="state"
            >
              <span className="material-symbols-outlined text-[15px] text-[#c25ee8]">
                location_on
              </span>{' '}
              <span className="truncate">¿En qué estado?</span>
            </label>
            <div className="relative">
              <button
                type="button"
                id="state"
                onClick={() => setIsStateModalOpen(true)}
                className={`${styles.countrySelectorButton} ${
                  errors.departamento_estado ? styles.inputError : ''
                }`}
                aria-haspopup="dialog"
                aria-expanded={isStateModalOpen}
              >
                <div className="flex items-center min-w-0 pr-1">
                  <span
                    className={`text-xs sm:text-sm truncate ${
                      selectedState ? 'text-gray-800 font-medium' : 'text-gray-400'
                    }`}
                  >
                    {selectedState || 'Selecciona estado'}
                  </span>
                </div>
                <span className="material-symbols-outlined text-[15px] text-gray-400 pointer-events-none flex-shrink-0">
                  expand_more
                </span>
              </button>
            </div>
            {errors.departamento_estado && (
              <p className="text-[10px] font-medium text-red-500 pl-1 mt-0.5">
                {errors.departamento_estado.message}
              </p>
            )}
          </div>
        </div>

        {/* Municipio o Ciudad (Sin icono interno) */}
        <div className="flex flex-col gap-1">
          <label
            className="flex items-center gap-2 text-xs font-bold text-gray-700 mb-1"
            htmlFor="city"
          >
            <span className="material-symbols-outlined text-[15px] text-[#c25ee8]">
              apartment
            </span>{' '}
            <span>¿Cuál es tu ciudad o municipio?</span>
          </label>
          <div className="relative">
            <button
              type="button"
              id="city"
              onClick={() => setIsCityModalOpen(true)}
              className={`${styles.countrySelectorButton} ${
                errors.municipio_ciudad ? styles.inputError : ''
              }`}
              aria-haspopup="dialog"
              aria-expanded={isCityModalOpen}
            >
              <div className="flex items-center min-w-0 pr-1">
                <span
                  className={`text-xs sm:text-sm truncate ${
                    selectedCity ? 'text-gray-800 font-medium' : 'text-gray-400'
                  }`}
                >
                  {selectedCity || 'Ingresa tu ciudad'}
                </span>
              </div>
              <span className="material-symbols-outlined text-[15px] text-gray-400 pointer-events-none flex-shrink-0">
                expand_more
              </span>
            </button>
          </div>
          {errors.municipio_ciudad && (
            <p className="text-[10px] font-medium text-red-500 pl-1 mt-0.5">
              {errors.municipio_ciudad.message}
            </p>
          )}
        </div>

        {/* Botón Continuar */}
        <div className="pt-2">
          <button
            type="submit"
            disabled={isSubmittingLocal}
            className={styles.submitBtn}
          >
            <span>{isSubmittingLocal ? 'Guardando...' : 'Continuar'}</span>
            <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
          </button>
        </div>

        {/* Enlace de ayuda "¿Por qué me piden estos datos?" */}
        <div className="text-center pt-1">
          <button
            type="button"
            onClick={() => setShowInfoModal(true)}
            className="text-[11px] text-gray-500 hover:text-[#c25ee8] inline-flex items-center gap-1 transition cursor-pointer select-none"
          >
            <span className="material-symbols-outlined text-[13px] text-[#c25ee8]">help</span>
            <span>¿Por qué me piden estos datos?</span>
          </button>
        </div>

        {/* Enlace "¿Ya tienes cuenta? Inicia sesión" */}
        <div className="mt-2 text-center">
          <p className="text-xs text-gray-600">
            ¿Ya tienes cuenta?{' '}
            <Link
              href="/login"
              className="text-[#c25ee8] font-bold hover:underline ml-0.5"
            >
              Inicia sesión
            </Link>
          </p>
        </div>

        {/* Espacio en blanco móvil para que el botón de Continuar suba cómodamente */}
        <div className="w-full h-8 sm:h-12 md:hidden pointer-events-none select-none" aria-hidden="true" />

        {/* Copyright Desktop */}
        <p className="text-center text-[10px] text-gray-400 mt-2 font-normal tracking-wide hidden sm:block">
          © 2026 AviFácil. Todos los derechos reservados.
        </p>
      </form>

      {/* ─── Modal Informativo "¿Por qué te pedimos estos datos?" (Estilo AviFácil) ─── */}
      <PorQuePedimosDatosModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
      />

      {/* ─── Modal de Selección de País (diseño desing-modal-paises.html) ─── */}
      <PaisModalSelector
        isOpen={isCountryModalOpen}
        selectedCountryName={selectedCountry}
        onSelect={handleSelectCountryFromModal}
        onClose={() => setIsCountryModalOpen(false)}
      />

      {/* ─── Modal de Selección de Estado (diseño desing-modal-estado.html) ─── */}
      <EstadoModalSelector
        isOpen={isStateModalOpen}
        selectedStateName={selectedState}
        countryName={selectedCountry}
        states={states}
        isLoading={isLoadingStates}
        onSelect={handleSelectStateFromModal}
        onClose={() => setIsStateModalOpen(false)}
        onOpenCountryModal={() => setIsCountryModalOpen(true)}
      />

      {/* ─── Modal de Selección de Ciudad (diseño design.moal-ciudad.html) ─── */}
      <CiudadModalSelector
        isOpen={isCityModalOpen}
        selectedCityName={selectedCity}
        countryName={selectedCountry}
        stateName={selectedState}
        cities={cities}
        isLoading={isLoadingCities}
        onSelect={handleSelectCityFromModal}
        onClose={() => setIsCityModalOpen(false)}
        onOpenStateModal={() => setIsStateModalOpen(true)}
      />
    </div>
  );
}
